/**
 * Parse an ISO datetime string from the API as UTC.
 *
 * The backend stores naive UTC datetimes and Pydantic serializes them
 * without the "Z" suffix (e.g. "2026-04-12T23:24:53").  JavaScript's
 * Date constructor treats such strings as **local** time, which causes
 * times to display with the wrong offset.  Appending "Z" forces UTC
 * interpretation so that toLocaleString() converts correctly.
 */
export function parseUTC(iso: string): Date {
  if (!iso.endsWith('Z') && !iso.includes('+') && !iso.includes('-', 10)) {
    return new Date(iso + 'Z')
  }
  return new Date(iso)
}

function timeZoneOffsetMs(instant: Date, timeZone: string): number {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(instant)
      .map((p) => [p.type, p.value]),
  )
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second),
  )
  return asUtc - instant.getTime()
}

/**
 * Interpret a wall-clock datetime (as produced by <input type="datetime-local">,
 * e.g. "2027-07-05T10:00") as local time of the given IANA timezone and return
 * the corresponding UTC instant. DST transitions are resolved by recomputing
 * the offset at the first guess.
 */
export function zonedTimeToUtc(wallClock: string, timeZone: string): Date {
  const normalized = wallClock.length === 16 ? `${wallClock}:00` : wallClock
  const guess = new Date(`${normalized}Z`)
  const offset = timeZoneOffsetMs(guess, timeZone)
  const utc = new Date(guess.getTime() - offset)
  const offsetAtUtc = timeZoneOffsetMs(utc, timeZone)
  return offsetAtUtc === offset ? utc : new Date(guess.getTime() - offsetAtUtc)
}

/**
 * Build the enrollment start_at payload value from the EnrollStartField
 * state: undefined for "start now", otherwise the wall-clock value in the
 * sequence's timezone converted to a UTC ISO string.
 */
export function scheduledStartToUtc(
  mode: 'now' | 'scheduled',
  wallClock: string,
  timeZone: string | undefined,
): string | undefined {
  if (mode !== 'scheduled' || !wallClock || !timeZone) return undefined
  return zonedTimeToUtc(wallClock, timeZone).toISOString()
}
